import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import Sidebar from '../components/admin/Sidebar';
import TopNavbar from '../components/admin/TopNavbar';
import './ContactRequests.css';

const ContactRequests = () => {
  const navigate = useNavigate();
  const [adminSession, setAdminSession] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    read: 0,
    responded: 0
  });

  useEffect(() => {
    const session = localStorage.getItem('adminSession') || sessionStorage.getItem('adminSession');
    if (!session) {
      navigate('/admin');
      return;
    }
    setAdminSession(JSON.parse(session));
  }, [navigate]);

  useEffect(() => {
    fetchMessages();
    fetchStats();
  }, [activeFilter, searchQuery]);

  const fetchStats = async () => {
    try {
      // Fetch total messages
      const { count: totalCount } = await supabase
        .from('contact_messages')
        .select('*', { count: 'exact', head: true });

      // Fetch new messages
      const { count: newCount } = await supabase
        .from('contact_messages')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'new');

      // Fetch read messages
      const { count: readCount } = await supabase
        .from('contact_messages')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'read');

      // Fetch responded messages
      const { count: respondedCount } = await supabase
        .from('contact_messages')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'responded');

      setStats({
        total: totalCount || 0,
        new: newCount || 0,
        read: readCount || 0,
        responded: respondedCount || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('contact_messages')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply status filter
      if (activeFilter !== 'All') {
        query = query.eq('status', activeFilter.toLowerCase());
      }

      // Apply search filter
      if (searchQuery.trim()) {
        query = query.or(`name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%,subject.ilike.%${searchQuery}%,message.ilike.%${searchQuery}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMessageClick = (message) => {
    setSelectedMessage(message);
    setIsDetailsModalOpen(true);
    
    // Mark as read if status is 'new'
    if (message.status === 'new') {
      updateMessageStatus(message.id, 'read');
    }
  };

  const updateMessageStatus = async (messageId, newStatus, responseNotes = null) => {
    try {
      const updateData = {
        status: newStatus,
        updated_at: new Date().toISOString()
      };

      if (newStatus === 'responded') {
        updateData.responded_at = new Date().toISOString();
        if (responseNotes) {
          updateData.response_notes = responseNotes;
        }
      }

      const { error } = await supabase
        .from('contact_messages')
        .update(updateData)
        .eq('id', messageId);

      if (error) throw error;

      // Refresh messages and stats
      fetchMessages();
      fetchStats();
      
      // Update selected message if it's the one being updated
      if (selectedMessage && selectedMessage.id === messageId) {
        setSelectedMessage({ ...selectedMessage, ...updateData });
      }
    } catch (error) {
      console.error('Error updating message status:', error);
      alert('Failed to update message status. Please try again.');
    }
  };

  const handleStatusChange = (messageId, newStatus) => {
    if (newStatus === 'responded') {
      const notes = prompt('Enter response notes (optional):');
      updateMessageStatus(messageId, newStatus, notes);
    } else {
      updateMessageStatus(messageId, newStatus);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'new':
        return 'contact-requests-status-new';
      case 'read':
        return 'contact-requests-status-read';
      case 'responded':
        return 'contact-requests-status-responded';
      case 'archived':
        return 'contact-requests-status-archived';
      default:
        return '';
    }
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (!adminSession) {
    return <div className="contact-requests-loading">Loading...</div>;
  }

  return (
    <div className="contact-requests-page">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <TopNavbar onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
      
      <div className="contact-requests-main">
        <div className="contact-requests-container">
          {/* Header */}
          <div className="contact-requests-header">
            <h1 className="contact-requests-title">Contact Requests</h1>
            <p className="contact-requests-subtitle">Manage customer inquiries and messages</p>
          </div>

          {/* Stats Cards */}
          <div className="contact-requests-stats">
            <div className="contact-requests-stat-card">
              <div className="contact-requests-stat-icon">📧</div>
              <div className="contact-requests-stat-content">
                <div className="contact-requests-stat-value">{stats.total}</div>
                <div className="contact-requests-stat-label">Total Messages</div>
              </div>
            </div>
            <div className="contact-requests-stat-card">
              <div className="contact-requests-stat-icon contact-requests-stat-icon-new">🔵</div>
              <div className="contact-requests-stat-content">
                <div className="contact-requests-stat-value">{stats.new}</div>
                <div className="contact-requests-stat-label">New</div>
              </div>
            </div>
            <div className="contact-requests-stat-card">
              <div className="contact-requests-stat-icon contact-requests-stat-icon-read">👁️</div>
              <div className="contact-requests-stat-content">
                <div className="contact-requests-stat-value">{stats.read}</div>
                <div className="contact-requests-stat-label">Read</div>
              </div>
            </div>
            <div className="contact-requests-stat-card">
              <div className="contact-requests-stat-icon contact-requests-stat-icon-responded">✓</div>
              <div className="contact-requests-stat-content">
                <div className="contact-requests-stat-value">{stats.responded}</div>
                <div className="contact-requests-stat-label">Responded</div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="contact-requests-filters">
            <div className="contact-requests-filter-tabs">
              {['All', 'New', 'Read', 'Responded', 'Archived'].map((filter) => (
                <button
                  key={filter}
                  className={`contact-requests-filter-tab ${activeFilter === filter ? 'active' : ''}`}
                  onClick={() => setActiveFilter(filter)}
                >
                  {filter}
                </button>
              ))}
            </div>
            <div className="contact-requests-search">
              <input
                type="text"
                placeholder="Search by name, email, subject, or message..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="contact-requests-search-input"
              />
            </div>
          </div>

          {/* Messages Table */}
          <div className="contact-requests-table-card">
            {loading ? (
              <div className="contact-requests-loading-state">Loading messages...</div>
            ) : messages.length === 0 ? (
              <div className="contact-requests-empty-state">
                <div className="contact-requests-empty-icon">📭</div>
                <p>No messages found</p>
              </div>
            ) : (
              <table className="contact-requests-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Subject</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((message) => (
                    <tr
                      key={message.id}
                      className={`contact-requests-table-row ${message.status === 'new' ? 'contact-requests-row-new' : ''}`}
                      onClick={() => handleMessageClick(message)}
                    >
                      <td className="contact-requests-table-name">{message.name}</td>
                      <td className="contact-requests-table-email">{message.email}</td>
                      <td className="contact-requests-table-phone">{message.phone || 'N/A'}</td>
                      <td className="contact-requests-table-subject">{message.subject || 'No Subject'}</td>
                      <td className="contact-requests-table-message">{truncateText(message.message, 80)}</td>
                      <td>
                        <span className={`contact-requests-status-badge ${getStatusBadgeClass(message.status)}`}>
                          {message.status.charAt(0).toUpperCase() + message.status.slice(1)}
                        </span>
                      </td>
                      <td className="contact-requests-table-date">{formatDate(message.created_at)}</td>
                      <td className="contact-requests-table-actions" onClick={(e) => e.stopPropagation()}>
                        <select
                          className="contact-requests-status-select"
                          value={message.status}
                          onChange={(e) => handleStatusChange(message.id, e.target.value)}
                        >
                          <option value="new">New</option>
                          <option value="read">Read</option>
                          <option value="responded">Responded</option>
                          <option value="archived">Archived</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Message Details Modal */}
      {isDetailsModalOpen && selectedMessage && (
        <div className="contact-requests-modal-overlay" onClick={() => setIsDetailsModalOpen(false)}>
          <div className="contact-requests-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="contact-requests-modal-header">
              <h2>Message Details</h2>
              <button
                className="contact-requests-modal-close"
                onClick={() => setIsDetailsModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="contact-requests-modal-body">
              <div className="contact-requests-modal-section">
                <div className="contact-requests-modal-row">
                  <div className="contact-requests-modal-label">Name:</div>
                  <div className="contact-requests-modal-value">{selectedMessage.name}</div>
                </div>
                <div className="contact-requests-modal-row">
                  <div className="contact-requests-modal-label">Email:</div>
                  <div className="contact-requests-modal-value">
                    <a href={`mailto:${selectedMessage.email}`}>{selectedMessage.email}</a>
                  </div>
                </div>
                {selectedMessage.phone && (
                  <div className="contact-requests-modal-row">
                    <div className="contact-requests-modal-label">Phone:</div>
                    <div className="contact-requests-modal-value">
                      <a href={`tel:${selectedMessage.phone}`}>{selectedMessage.phone}</a>
                    </div>
                  </div>
                )}
                {selectedMessage.subject && (
                  <div className="contact-requests-modal-row">
                    <div className="contact-requests-modal-label">Subject:</div>
                    <div className="contact-requests-modal-value">{selectedMessage.subject}</div>
                  </div>
                )}
                <div className="contact-requests-modal-row">
                  <div className="contact-requests-modal-label">Status:</div>
                  <div className="contact-requests-modal-value">
                    <span className={`contact-requests-status-badge ${getStatusBadgeClass(selectedMessage.status)}`}>
                      {selectedMessage.status.charAt(0).toUpperCase() + selectedMessage.status.slice(1)}
                    </span>
                  </div>
                </div>
                <div className="contact-requests-modal-row">
                  <div className="contact-requests-modal-label">Date:</div>
                  <div className="contact-requests-modal-value">{formatDate(selectedMessage.created_at)}</div>
                </div>
              </div>
              <div className="contact-requests-modal-section">
                <div className="contact-requests-modal-label">Message:</div>
                <div className="contact-requests-modal-message">{selectedMessage.message}</div>
              </div>
              {selectedMessage.response_notes && (
                <div className="contact-requests-modal-section">
                  <div className="contact-requests-modal-label">Response Notes:</div>
                  <div className="contact-requests-modal-message">{selectedMessage.response_notes}</div>
                </div>
              )}
              {selectedMessage.responded_at && (
                <div className="contact-requests-modal-row">
                  <div className="contact-requests-modal-label">Responded At:</div>
                  <div className="contact-requests-modal-value">{formatDate(selectedMessage.responded_at)}</div>
                </div>
              )}
              <div className="contact-requests-modal-actions">
                <select
                  className="contact-requests-status-select"
                  value={selectedMessage.status}
                  onChange={(e) => {
                    handleStatusChange(selectedMessage.id, e.target.value);
                    setIsDetailsModalOpen(false);
                  }}
                >
                  <option value="new">New</option>
                  <option value="read">Read</option>
                  <option value="responded">Responded</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactRequests;





